import React, {useState} from "react";
import btorageBooster from "../../../assets/img/btorageBooster.png";
import {BUTTON_GUIDE} from "./storeApiDocData";
import {Notice} from "./StoreUI";
import {TbCopy, TbCheck} from "react-icons/tb";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  비토리지 API 버튼 가이드라인
 * ════════════════════════════════════════════════════════════════════════════════
 *  - 버튼 형식은 {(btorageBooster.png) Delivery} 로 고정된다.
 *  - 테마(Brand/White/Black) × 모양(Round/Soft/Square) × 크기(sm/md/lg) 조합을
 *    실제로 렌더링해 보여주고, 고른 조합의 삽입 스크립트를 바로 복사할 수 있다.
 *  - 여기 렌더링되는 버튼은 실제 배포 스크립트(public/btorage-button.js)와 같은 값을 쓴다.
 *    (Brand 문구색 #FFFFFF / 바탕 #FFBE48)
 * ════════════════════════════════════════════════════════════════════════════════
 */

/** 옵션 선택줄 — 하나의 트랙 위에서 선택된 칸만 여명으로 켜지는 세그먼트 */
const OptionRow = ({label, options, value, onChange}) => (
    <div className="tw-mb-4">
        <p className="st-eyebrow tw-mb-2">{label}</p>
        <div className="st-tabs st-tabs-sm">
            {options.map(opt => (
                <button key={opt.key} type="button" onClick={() => onChange(opt.key)}
                        aria-pressed={value === opt.key}
                        className="st-tab">
                    {opt.label}
                </button>
            ))}
        </div>
    </div>
);

function StoreButtonGuide({apiKey}) {

    const [theme, setTheme] = useState('brand');
    const [shape, setShape] = useState('round');
    const [size, setSize] = useState('md');
    const [copied, setCopied] = useState(false);

    const themeObj = BUTTON_GUIDE.themes.find(t => t.key === theme) || BUTTON_GUIDE.themes[0];
    const shapeObj = BUTTON_GUIDE.shapes.find(s => s.key === shape) || BUTTON_GUIDE.shapes[0];
    const sizeObj = BUTTON_GUIDE.sizes.find(s => s.key === size) || BUTTON_GUIDE.sizes[1];

    /** 실제 버튼 렌더 (btorage-button.js 가 생성하는 마크업과 동일한 형태) */
    const renderButton = (t, s, z) => (
        <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "7px",
            height: `${z.height}px`, padding: `0 ${z.padding}px`,
            borderRadius: s.radius, background: t.bg, color: t.fg, border: t.border,
            fontSize: `${z.font}px`, fontWeight: 700, cursor: "pointer", userSelect: "none",
            fontFamily: "'LINESeedKR-Bd', 'Malgun Gothic', sans-serif",
            boxShadow: t.key === 'white' ? "0 1px 3px rgba(0,0,0,0.08)" : "0 6px 16px -10px rgba(0,0,0,0.5)",
            whiteSpace: "nowrap",
        }}>
            <img src={btorageBooster} alt="Btorage"
                 style={{height: `${z.logo}px`, objectFit: "contain", display: "block"}}/>
            Delivery
        </span>
    );

    const snippet =
        `<script src="https://btorage.com/btorage-button.js"
        data-btorage-key="${apiKey || 'YOUR_API_KEY'}"
        data-btorage-theme="${theme}"
        data-btorage-shape="${shape}"
        data-btorage-size="${size}"
        defer></script>`;

    const copySnippet = async () => {
        try {
            await navigator.clipboard.writeText(snippet);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch (e) { /* 무시 */ }
    };

    return (
        <div>
            {/*═══ 미리보기 + 옵션 ═══*/}
            <section className="tw-mb-9">
                <h3 className="st-display tw-text-[16.5px] tw-text-[color:var(--st-text)]">버튼 만들기</h3>
                <p className="tw-mb-4 tw-mt-1.5 tw-text-[12.5px] tw-leading-[1.8] tw-text-[color:var(--st-text-faint)] tw-break-keep">
                    쇼핑몰 분위기에 맞는 조합을 고르면 바로 붙여넣을 스크립트를 만들어 드립니다
                </p>

                {/* 무대 — 버튼이 여명 위에 떠 있다 */}
                <div className="tw-mb-5 tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-[18px] tw-border tw-px-5 tw-py-10"
                     style={{
                         borderColor: 'var(--st-glass-line)',
                         background: theme === 'black'
                             ? 'linear-gradient(135deg, #EDEAF6, #E2DEF0)'
                             : 'linear-gradient(135deg, rgba(255,240,210,.9), rgba(239,233,255,.9))',
                     }}>
                    {renderButton(themeObj, shapeObj, sizeObj)}
                </div>

                <OptionRow label="테마" options={BUTTON_GUIDE.themes} value={theme} onChange={setTheme}/>
                <OptionRow label="모양" options={BUTTON_GUIDE.shapes} value={shape} onChange={setShape}/>
                <OptionRow label="크기" options={BUTTON_GUIDE.sizes} value={size} onChange={setSize}/>

                <p className="tw-mb-4 tw-mt-1 tw-text-[12px] tw-text-[color:var(--st-text-faint)] tw-break-keep">
                    {themeObj.desc}
                </p>

                <div className="tw-relative">
                    <pre className="st-mono st-scroll tw-overflow-x-auto tw-rounded-[16px] tw-border tw-px-4 tw-py-4 tw-text-[12px] tw-leading-[1.8] tw-text-[color:var(--st-text)]"
                         style={{background: 'var(--st-field)', borderColor: 'var(--st-glass-line)', whiteSpace: 'pre'}}>
                        {snippet}
                    </pre>
                    <button type="button" onClick={copySnippet}
                            className="st-btn st-btn-glass st-btn-sm tw-absolute tw-right-3 tw-top-3">
                        {copied ? <><TbCheck/> 복사됨</> : <><TbCopy/> 복사</>}
                    </button>
                </div>
            </section>

            {/*═══ 전체 카탈로그 ═══*/}
            <section className="tw-mb-9">
                <h3 className="st-display tw-text-[16.5px] tw-text-[color:var(--st-text)]">전체 버튼 카탈로그</h3>
                <p className="tw-mb-4 tw-mt-1.5 tw-text-[12.5px] tw-text-[color:var(--st-text-faint)]">
                    테마 3종 × 모양 3종 × 크기 3종 = 총 27가지 조합을 지원합니다
                </p>

                <div className="tw-space-y-2.5">
                    {BUTTON_GUIDE.themes.map(t => (
                        <div key={t.key} className="tw-rounded-[18px] tw-border tw-px-5 tw-py-4"
                             style={{
                                 borderColor: 'var(--st-glass-line)',
                                 background: t.key === 'black'
                                     ? 'linear-gradient(135deg, #EDEAF6, #E4E0F2)'
                                     : 'linear-gradient(135deg, rgba(255,245,228,.75), rgba(243,239,255,.75))',
                             }}>
                            <p className="tw-mb-3.5 tw-text-[12px] tw-font-bold tw-text-[#5B4F86]">{t.label}</p>
                            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3.5">
                                {BUTTON_GUIDE.shapes.map(s =>
                                    BUTTON_GUIDE.sizes.map(z => (
                                        <div key={`${t.key}-${s.key}-${z.key}`} className="tw-text-center">
                                            {renderButton(t, s, z)}
                                            <p className="st-mono tw-mt-1.5 tw-text-[10px] tw-text-[#8C82AD]">
                                                {s.key} · {z.key}
                                            </p>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/*═══ 사용 규칙 ═══*/}
            <section>
                <h3 className="st-display tw-mb-4 tw-text-[16.5px] tw-text-[color:var(--st-text)]">사용 규칙</h3>

                <ol className="tw-space-y-2.5">
                    {BUTTON_GUIDE.rules.map((r, i) => (
                        <li key={i} className="tw-flex tw-gap-3">
                            <span className="st-num tw-mt-[1px] tw-inline-flex tw-h-[22px] tw-w-[22px] tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-[11px] tw-font-bold"
                                  style={{background: 'rgba(203,182,255,.22)', color: '#7457C9'}}>
                                {i + 1}
                            </span>
                            <span className="tw-text-[13px] tw-leading-[1.8] tw-text-[color:var(--st-text-sub)] tw-break-keep">{r}</span>
                        </li>
                    ))}
                </ol>

                <div className="tw-mt-5">
                    <Notice tone="warn">
                        위 규칙을 지키지 않은 버튼이 확인되면 연동이 중단될 수 있습니다.
                        디자인 관련 문의는 [채팅문의] 또는 help@btorage.com 으로 남겨주세요.
                    </Notice>
                </div>
            </section>
        </div>
    );
}

export default StoreButtonGuide;
